import {
  NOTIFY_OUTCOME_LABELS,
  NOTIFY_SUBJECT_LABELS,
  failureSentence,
  type DeliveryFailureCode,
  type DeliveryRow,
  type EmailDeliveryView,
  type EmailKeyStatus,
  type NotifyChannel,
  type NotifySubject,
  type QuietRunRow,
  type TestStatusView,
} from "@shared/types/notifications";
import { formatInstant } from "@/lib/time/displayTime";
import { byCodeUnit } from "@shared/sort";

/**
 * Every sentence the notification screens write from a server value (contract-notif §4.4,
 * §4.5), in one pure module so each form is tested once and a screen never assembles one by
 * hand. The closed tables (failure codes, subject help) live in `shared/types/notifications.ts`
 * and are imported, never retyped. No sentence here names an environment variable (T-S7).
 *
 * Every time is in the reader's display zone and names it (round 3, 2026-09-30: "UTC doesn't
 * help the user"): each function that writes one takes `zone` (`useDisplayTz()`). The emails
 * and Teams cards themselves are the runner's: the recipient's chosen zone, else Pacific (#1019).
 */

/** A time in a sentence: "Sep 30, 11:42 AM IST". */
function when(iso: string, zone: string): string {
  return formatInstant(iso, zone, "short", { named: true });
}

/** A time within the hour: "02:10 PM IST". */
function clock(iso: string, zone: string): string {
  return formatInstant(iso, zone, "time", { named: true });
}

// ---------------------------------------------------------------------------------------------
// The Email delivery card (§4.3, §4.4)
// ---------------------------------------------------------------------------------------------

const KEY_CHECK_SENTENCES: Record<Exclude<EmailKeyStatus, "not_configured">, string> = {
  valid: "Accepted by SendGrid.",
  missing_scope:
    "SendGrid accepted the key, but it lacks the Mail Send permission. Create a key with Mail Send and replace the secret in {vault}.",
  rejected:
    "SendGrid refused this key. Replace the secret in {vault}. The runner picks up the new version within 15 minutes.",
  unreachable: "The runner could not reach SendGrid. It checks again every 15 minutes.",
  unreadable:
    "The runner could not read the secret from {vault}. Check that it exists and that the runner can read it. It checks again every 15 minutes.",
};

export const KEY_NOT_CONFIGURED =
  "Not configured. The runner has no reference to a SendGrid API key. See docs/deploy/notifications.md.";

export const RUNNER_NOT_REPORTED =
  "Email status has not been reported yet. It is reported when skyData starts and every 15 minutes.";

/** A fact the runner has not reported: never read as Not configured, None or zero (#1088). */
export const UNKNOWN = "Unknown";

export const SENDER_NOT_CONFIGURED =
  "Sender not configured. Set NOTIFY_EMAIL_FROM. See docs/deploy/notifications.md.";

function vaultOrDefault(vault: string | null | undefined): string {
  return vault ? vault : "the platform vault";
}

/** "Key check" on the card, with "Last checked <time>." (§4.4). */
export function keyCheckSentence(
  view: Pick<EmailDeliveryView, "keyStatus" | "vaultName" | "checkedAt">,
  zone: string,
): string {
  const status = view.keyStatus ?? "not_configured";
  if (status === "not_configured") return KEY_NOT_CONFIGURED;
  const sentence = KEY_CHECK_SENTENCES[status].replace("{vault}", () =>
    vaultOrDefault(view.vaultName),
  );
  return view.checkedAt ? `${sentence} Last checked ${when(view.checkedAt, zone)}.` : sentence;
}

/** "Last failure": "{time}: {sentence}" or "None". */
export function lastFailureSentence(
  view: Pick<EmailDeliveryView, "lastFailureAt" | "lastFailureCode" | "vaultName">,
  zone: string,
): string {
  if (!view.lastFailureAt || !view.lastFailureCode) return "None";
  return `${when(view.lastFailureAt, zone)}: ${failureSentence(view.lastFailureCode, "email", {
    vault: view.vaultName,
  })}`;
}

export function last24hSentence(counts: EmailDeliveryView["last24h"]): string {
  const base = `${counts.sent} sent, ${counts.failed} failed, ${counts.waiting} waiting`;
  // #463: parked emails are named whenever there are any, so the counts never hide them.
  return counts.parked > 0 ? `${base}, ${counts.parked} kept until released` : base;
}

// ---------------------------------------------------------------------------------------------
// The platform test email (§4.5, "Test email dialog")
// ---------------------------------------------------------------------------------------------

/**
 * #2158: the give-up line after 90 s. The dialog keeps reading the status while it is open, so a
 * delivery that ends later is still said there; this line names no internals.
 */
export const EMAIL_TEST_WAITING_TOO_LONG =
  "Still waiting for it to be sent. This dialog shows the result when it arrives, and the card's Last successful send changes when it is sent.";

export const EMAIL_TEST_NO_ACCOUNT_EMAIL =
  "Your account has no email address, so there is nowhere to send a test email.";

/** #2165: the L1 Teams test's give-up line, with the same rule as the email's. */
export const L1_TEAMS_TEST_WAITING_TOO_LONG =
  "Still waiting for it to be posted. This dialog shows the result when it arrives.";

export const L1_TEAMS_TEST_NOT_CONFIGURED =
  "Not sent. No L1 Teams webhook is stored. A platform admin sets one on this card with Set webhook.";

/** Words for the platform tests' own codes, the same for both channels. */
const PLATFORM_TEST_SHARED = {
  tenant_disabled: "Not sent. The tenant is disabled.",
  outside_allowed_domains: "Not sent. The address is outside the allowed domains.",
  grant_inactive: "Not sent. The access this was for was removed.",
  user_disabled: "Not sent. The person was deactivated.",
  // A delivery whose facts do not parse ends here on its first try, so no "more than once".
  internal:
    "Not sent. Sending stopped unexpectedly. Try again, and if it happens again, contact Skypoint support.",
} as const;

/**
 * #2158: the test email's fixed sentence for every code a delivery can end with, each naming
 * what happened and who fixes it. `{vault}` is the vault the key is read from, `{sender}` the
 * verified sender. Keyed by every code, so a new code cannot reach the dialog without words.
 */
const EMAIL_TEST_FAILURES = {
  ...PLATFORM_TEST_SHARED,
  not_configured:
    "Not sent. Email is not set up for this deployment. See docs/deploy/notifications.md.",
  key_rejected:
    "Not sent. SendGrid refused the key. Replace it with a key that has Mail Send permission. A platform admin replaces the secret in {vault}, and the new key is used within 15 minutes.",
  key_missing:
    "Not sent. The SendGrid API key does not exist in {vault}. A platform admin adds it there. The email is kept until a platform operator releases it.",
  sender_unverified:
    "Not sent. SendGrid refused {sender} because it is not a verified sender for this key. A platform admin verifies the sender in SendGrid.",
  vault_unreadable:
    "Not sent. The SendGrid API key could not be read from {vault}. A platform admin checks that the secret exists there.",
  payload_rejected:
    "Not sent. SendGrid refused the message. Try again, and if it happens again, contact Skypoint support.",
  rate_limited:
    "Not sent. SendGrid kept answering that too many messages were sent. Try again in an hour.",
  upstream_error: "Not sent. SendGrid answered with an error on every try. Try again later.",
  timeout: "Not sent. SendGrid did not answer in time on every try. Try again later.",
  network:
    "Not sent. SendGrid could not be reached. A platform admin checks that this deployment can reach api.sendgrid.com.",
  webhook_rejected: PLATFORM_TEST_SHARED.internal,
  host_refused:
    "Not sent. The SendGrid address is not one this deployment can post to. Contact Skypoint support.",
} as const satisfies Record<DeliveryFailureCode, string>;

/** #2165: the L1 Teams test's fixed sentence for every code, with who fixes it. */
const L1_TEAMS_TEST_FAILURES = {
  ...PLATFORM_TEST_SHARED,
  not_configured: L1_TEAMS_TEST_NOT_CONFIGURED,
  webhook_rejected:
    "Not sent. Teams refused the webhook. A platform admin replaces it on this card with a new webhook from the L1 channel.",
  host_refused:
    "Not sent. The webhook address is not a Teams address this deployment can post to. A platform admin replaces it on this card with a webhook made in Teams.",
  // Any vault read that fails (access, network, a missing secret), so the check comes first.
  vault_unreadable:
    "Not sent. The L1 Teams webhook could not be read from the deployment's Key Vault. A platform admin checks that the secret exists there, or replaces the webhook on this card.",
  payload_rejected:
    "Not sent. Teams refused the test card. Try again, and if it happens again, contact Skypoint support.",
  rate_limited:
    "Not sent. Teams kept answering that too many messages were sent. Try again in an hour.",
  upstream_error: "Not sent. Teams answered with an error on every try. Try again later.",
  timeout: "Not sent. Teams did not answer in time on every try. Try again later.",
  network:
    "Not sent. Teams could not be reached. A platform admin checks that this deployment can reach the webhook's address.",
  key_rejected: PLATFORM_TEST_SHARED.internal,
  key_missing: PLATFORM_TEST_SHARED.internal,
  sender_unverified: PLATFORM_TEST_SHARED.internal,
} as const satisfies Record<DeliveryFailureCode, string>;

/** Exported for the copy test: every code has words on both platform tests. */
export const PLATFORM_TEST_FAILURES = {
  email: EMAIL_TEST_FAILURES,
  teams: L1_TEAMS_TEST_FAILURES,
} as const satisfies Record<NotifyChannel, Record<DeliveryFailureCode, string>>;

interface PlatformTestContext {
  vault?: string | null;
  sender?: string | null;
}

/** The fixed "Not sent." sentence for a platform test that ended on `code` (null: internal). */
export function platformTestFailureSentence(
  channel: NotifyChannel,
  code: DeliveryFailureCode | null,
  context: PlatformTestContext = {},
): string {
  const sentence: string = PLATFORM_TEST_FAILURES[channel][code ?? "internal"];
  const sender = context.sender ? `the sender ${context.sender}` : "the sender";
  return sentence
    .replace("{vault}", () => vaultOrDefault(context.vault))
    .replace("{sender}", () => sender);
}

/**
 * #2158: a try that failed while the delivery is still tried again, per channel and code. The
 * cause only, never "every try", "more than once" or "Try again": the delivery is retried by
 * itself (a refused key once more after 90 s, any other retried code on the backoff schedule).
 * Keyed by every code, so a new code cannot reach the line without words.
 */
const SHARED_RETRY_CAUSES = {
  tenant_disabled: "The tenant is disabled.",
  outside_allowed_domains: "The address is outside the allowed domains.",
  grant_inactive: "The access this was for was removed.",
  user_disabled: "The person was deactivated.",
  internal: "Sending stopped unexpectedly.",
} as const;

const EMAIL_RETRY_CAUSES = {
  ...SHARED_RETRY_CAUSES,
  not_configured: "Email is not set up for this deployment.",
  key_rejected:
    "SendGrid refused the key. A platform admin replaces the secret in {vault} with a key that has Mail Send permission, and the new key is used within 15 minutes.",
  key_missing: "The SendGrid API key does not exist in {vault}.",
  sender_unverified: "SendGrid refused {sender} because it is not a verified sender for this key.",
  vault_unreadable: "The SendGrid API key could not be read from {vault}.",
  payload_rejected: "SendGrid refused the message.",
  rate_limited: "SendGrid answered that too many messages were sent.",
  upstream_error: "SendGrid answered with an error.",
  timeout: "SendGrid did not answer in time.",
  network: "SendGrid could not be reached.",
  webhook_rejected: SHARED_RETRY_CAUSES.internal,
  host_refused: "The SendGrid address is not one this deployment can post to.",
} as const satisfies Record<DeliveryFailureCode, string>;

const TEAMS_RETRY_CAUSES = {
  ...SHARED_RETRY_CAUSES,
  not_configured: "No Teams webhook is stored.",
  webhook_rejected: "Teams refused the webhook.",
  host_refused: "The webhook address is not a Teams address this deployment can post to.",
  vault_unreadable: "The Teams webhook could not be read from Key Vault.",
  payload_rejected: "Teams refused the message.",
  rate_limited: "Teams answered that too many messages were sent.",
  upstream_error: "Teams answered with an error.",
  timeout: "Teams did not answer in time.",
  network: "Teams could not be reached.",
  key_rejected: SHARED_RETRY_CAUSES.internal,
  key_missing: SHARED_RETRY_CAUSES.internal,
  sender_unverified: SHARED_RETRY_CAUSES.internal,
} as const satisfies Record<DeliveryFailureCode, string>;

/** Exported for the copy test: every code has retrying words on both channels. */
export const RETRY_CAUSES = {
  email: EMAIL_RETRY_CAUSES,
  teams: TEAMS_RETRY_CAUSES,
} as const satisfies Record<NotifyChannel, Record<DeliveryFailureCode, string>>;

/**
 * The line while a test (platform or instance) is still being tried and a try already failed:
 * "Not sent yet. The last try failed. <cause> It is tried again automatically." Null while no
 * try has failed yet: the line says "Sending…".
 */
export function testRetryingSentence(
  status: Pick<TestStatusView, "status" | "deliveries">,
  context: PlatformTestContext = {},
): string | null {
  if (status.status !== "waiting") return null;
  const failed = status.deliveries.find(
    (d) => (d.status === "pending" || d.status === "sending") && d.failureCode !== null,
  );
  if (!failed?.failureCode) return null;
  const sender = context.sender ? `the sender ${context.sender}` : "the sender";
  const cause = RETRY_CAUSES[failed.channel][failed.failureCode]
    .replace("{vault}", () => vaultOrDefault(context.vault))
    .replace("{sender}", () => sender);
  return `Not sent yet. The last try failed. ${cause} It is tried again automatically.`;
}

/**
 * #2158: a platform test's retrying line, read on the dialog's own channel (its one delivery is
 * on that channel).
 */
export function platformTestRetryingSentence(
  channel: NotifyChannel,
  status: Pick<TestStatusView, "status" | "deliveries">,
  context: PlatformTestContext = {},
): string | null {
  return testRetryingSentence(
    {
      status: status.status,
      deliveries: status.deliveries.map((d) => ({ ...d, channel })),
    },
    context,
  );
}

/** The dialog's result line once the status route answers something other than `waiting`. */
export function emailTestResultSentence(
  status: Pick<TestStatusView, "status" | "failureCode" | "deliveries">,
  context: { maskedTo: string | null; vault: string | null; sender: string | null },
  zone: string,
): string {
  if (status.status === "sent") {
    const sentAt = status.deliveries.find((d) => d.sentAt)?.sentAt ?? null;
    const to = context.maskedTo ?? "your own address";
    const at = sentAt ? ` at ${when(sentAt, zone)}` : "";
    return `Sent to ${to}${at}. Check the inbox and the spam folder.`;
  }
  if (status.status === "not_configured") return EMAIL_TEST_FAILURES.not_configured;
  return platformTestFailureSentence("email", status.failureCode, context);
}

/** #2165: the L1 Teams test's result line once the status route answers something else. */
export function l1TeamsTestResultSentence(
  status: Pick<TestStatusView, "status" | "failureCode" | "deliveries">,
  zone: string,
): string {
  if (status.status === "sent") {
    const sentAt = status.deliveries.find((d) => d.sentAt)?.sentAt ?? null;
    const at = sentAt ? ` at ${when(sentAt, zone)}` : "";
    return `Posted to the L1 Teams channel${at}. Check the channel for the test card.`;
  }
  if (status.status === "not_configured") return L1_TEAMS_TEST_NOT_CONFIGURED;
  return platformTestFailureSentence("teams", status.failureCode);
}

export const L1_TEAMS_TEST_CAP = "Five tests of the L1 Teams channel were sent in the last hour.";

/** "Five tests ... Try again at 03:01 PM IST." from the 429's `retryAt`. */
export function l1TeamsTestRateLimitedSentence(retryAt: string | null, zone: string): string {
  return retryAt ? `${L1_TEAMS_TEST_CAP} Try again at ${clock(retryAt, zone)}.` : L1_TEAMS_TEST_CAP;
}

// ---------------------------------------------------------------------------------------------
// The instance test (§4.5, "Test button")
// ---------------------------------------------------------------------------------------------

/**
 * #2158: the instance test's give-up line after 90 s. The line keeps reading the status while the
 * tab is open, so a delivery that ends later is still said there. Names no internals (#929).
 */
export const INSTANCE_TEST_WAITING_TOO_LONG =
  "Still waiting for it to be sent. This line shows the result when it arrives, and so does Recent deliveries.";

export const INSTANCE_TEST_NO_TARGETS =
  "This rule has no one to notify yet. Add an address or turn on the Teams channel, then save.";

function addressCount(n: number): string {
  return n === 1 ? "1 address" : `${n} addresses`;
}

type TestDelivery = TestStatusView["deliveries"][number];

/** A delivery the runner skipped because its channel is not set up. */
function isNotConfiguredSkip(d: TestDelivery): boolean {
  return d.status === "skipped" && d.failureCode === "not_configured";
}

/** The newest `sentAt` among the deliveries, or null. */
function latestSentAt(deliveries: readonly TestDelivery[]): string | null {
  return (
    deliveries
      .map((d) => d.sentAt)
      .filter((at): at is string => Boolean(at))
      .sort(byCodeUnit)
      .at(-1) ?? null
  );
}

/** Who a sent test reached: "2 addresses and the Teams channel", "1 address", "the Teams channel". */
function sentRecipients(emailsSent: number, teamsSent: boolean): string {
  if (emailsSent > 0 && teamsSent) return `${addressCount(emailsSent)} and the Teams channel`;
  if (emailsSent > 0) return addressCount(emailsSent);
  return "the Teams channel";
}

function sentTestSentence(deliveries: readonly TestDelivery[], zone: string): string {
  const emails = deliveries.filter((d) => d.channel === "email");
  const teams = deliveries.filter((d) => d.channel === "teams");
  const emailsSent = emails.filter((d) => d.status === "sent").length;
  const teamsSent = teams.some((d) => d.status === "sent");
  const sentAt = latestSentAt(deliveries);
  const to = sentRecipients(emailsSent, teamsSent);
  const at = sentAt ? ` at ${when(sentAt, zone)}` : "";
  let sentence = `Sent to ${to}${at}.`;
  if (emails.some(isNotConfiguredSkip)) {
    sentence += " Email is not set up for this deployment, so no email was sent.";
  }
  if (teams.some(isNotConfiguredSkip)) {
    sentence += " No Teams webhook, so nothing was posted to Teams.";
  }
  // #1111: an address the tenant's Allowed notification domains left out is said here too, not
  // only in Recent deliveries.
  const outside = emails.filter(
    (d) => d.status === "skipped" && d.failureCode === "outside_allowed_domains",
  ).length;
  if (outside === 1) {
    sentence +=
      " 1 address is outside the allowed domains for your organization, so it was not sent.";
  } else if (outside > 1) {
    sentence += ` ${outside} addresses are outside the allowed domains for your organization, so they were not sent.`;
  }
  return sentence;
}

function notConfiguredTestSentence(deliveries: readonly TestDelivery[]): string {
  const emails = deliveries.filter((d) => d.channel === "email");
  const teams = deliveries.filter((d) => d.channel === "teams");
  const parts: string[] = [];
  if (emails.length > 0 || deliveries.length === 0) {
    parts.push("Email is not set up for this deployment");
  }
  if (teams.length > 0 || deliveries.length === 0) parts.push("this instance has no Teams webhook");
  const reason =
    parts.length === 2
      ? `${parts[0]}, and ${parts[1]}.`
      : `${(parts[0] ?? "").charAt(0).toUpperCase()}${(parts[0] ?? "").slice(1)}.`;
  return `Not sent: nothing is set up to deliver this rule. ${reason}`;
}

function failedTestSentence(
  deliveries: readonly TestDelivery[],
  failureCode: DeliveryFailureCode | null | undefined,
): string {
  const code: DeliveryFailureCode = failureCode ?? "internal";
  const channel: NotifyChannel =
    deliveries.find((d) => d.failureCode === code && d.status === "dead")?.channel ??
    deliveries.find((d) => d.failureCode === code)?.channel ??
    "teams";
  return `Not sent: ${failureSentence(code, channel)}`;
}

/** The line that replaces "Sending…" beside the button once the test is no longer waiting. */
export function instanceTestResultSentence(
  status: Pick<TestStatusView, "status" | "failureCode" | "deliveries">,
  zone: string,
): string {
  if (status.status === "sent") return sentTestSentence(status.deliveries, zone);
  if (status.status === "not_configured") return notConfiguredTestSentence(status.deliveries);
  return failedTestSentence(status.deliveries, status.failureCode);
}

// ---------------------------------------------------------------------------------------------
// Recent deliveries (§4.2, R10)
// ---------------------------------------------------------------------------------------------

export function deliveryWhat(row: Pick<DeliveryRow, "subject" | "eventKind">): string {
  if (row.subject) return NOTIFY_SUBJECT_LABELS[row.subject as NotifySubject];
  if (row.eventKind === "email_test") return "Email delivery";
  return EVENT_KIND_WHAT[row.eventKind] ?? "—";
}

/** What a delivery is about when it has no run subject: a schedule's busy skip is a Schedule. */
const EVENT_KIND_WHAT: Partial<Record<DeliveryRow["eventKind"], string>> = {
  schedule: "Schedules",
  credential: "Credentials",
  access_invite: "Access invitation",
  security: "Security",
  monitor: "Monitoring",
};

export function deliveryWhen(
  row: Pick<DeliveryRow, "outcome" | "recovered" | "cancelled">,
): string {
  // #1422: a cancelled run has no outcome of its own, and its email says Cancelled.
  if (row.cancelled) return "Cancelled";
  if (!row.outcome) return "—";
  if (row.recovered) return "Recovered";
  return NOTIFY_OUTCOME_LABELS[row.outcome];
}

/** The outcome as a noun in a sentence: "a success", "a failure", "a recovery". */
function outcomeNoun(row: Pick<QuietRunRow, "outcome" | "recovered">): string {
  if (row.recovered) return "a recovery";
  if (!row.outcome) return "this result";
  return `a ${NOTIFY_OUTCOME_LABELS[row.outcome].toLowerCase()}`;
}

/**
 * Round 4 item 3: why a run sent nothing. `no_rule`: nothing covered its outcome, which is what
 * a default Watch ("Failures and warnings") says to a success; `no_recipients`: something did,
 * but named no one.
 */
export function quietRunWhy(
  row: Pick<QuietRunRow, "reason" | "outcome" | "recovered" | "cancelled">,
): string {
  // #1422: only a cell, rule or Watch set to Every result hears about a cancelled run.
  if (row.cancelled) {
    return row.reason === "no_recipients"
      ? "A rule on Every result covers a cancelled run, but it names no one to send to"
      : "No rule or Watch on Every result covers a cancelled run";
  }
  return row.reason === "no_recipients"
    ? `A rule covers ${outcomeNoun(row)}, but it names no one to send to`
    : `No rule or Watch covers ${outcomeNoun(row)}`;
}

/** "mouseflow-sandbox", "a, b and 2 more": the connections a quiet run covered. */
export function quietRunConnections(
  row: Pick<QuietRunRow, "connectionNames" | "connectionsMore">,
): string {
  const names = row.connectionNames.join(", ");
  if (row.connectionsMore > 0) return `${names} and ${row.connectionsMore} more`;
  return names;
}

export function deliveryChannel(channel: NotifyChannel): string {
  return channel === "email" ? "Email" : "Teams";
}

/** The Status cell: "Sent", "Sent on attempt 3", "Retrying at 02:10 PM IST", "Failed: …", … */
export function deliveryStatusSentence(
  row: Pick<
    DeliveryRow,
    | "status"
    | "attempt"
    | "nextAttemptAt"
    | "failureCode"
    | "httpStatus"
    | "channel"
    | "outcome"
    | "repeatWindowMinutes"
  >,
  zone: string,
): string {
  switch (row.status) {
    case "sent":
      return row.attempt > 1 ? `Sent on attempt ${row.attempt}` : "Sent";
    case "sending":
      return "Sending…";
    case "pending":
      return row.attempt > 0 && row.nextAttemptAt
        ? `Retrying at ${clock(row.nextAttemptAt, zone)}`
        : "Waiting to send";
    case "held": {
      const what = row.outcome === "warning" ? "warning" : "failure";
      const minutes = row.repeatWindowMinutes ?? 0;
      return `Held back: the same ${what} within ${minutes} minutes`;
    }
    case "skipped":
      if (row.failureCode === "not_configured" || row.failureCode === null) {
        return row.channel === "email"
          ? "Not sent: email is not set up for this deployment"
          : "Not sent: no Teams webhook";
      }
      return `Not sent: ${failureSentence(row.failureCode, row.channel, { status: row.httpStatus })}`;
    case "dead":
      return `Failed: ${failureSentence(row.failureCode ?? "internal", row.channel, {
        status: row.httpStatus,
      })}`;
    case "parked":
      // #463: kept, never retried, until a platform operator releases it.
      return `Not sent: ${failureSentence(row.failureCode ?? "key_missing", row.channel)}`;
  }
}

// ---------------------------------------------------------------------------------------------
// Rate limits (§4.5): the 429's `details[0] = { field: "retryAt", issue: <ISO> }`
// ---------------------------------------------------------------------------------------------

export function retryAtOf(
  details: ReadonlyArray<{ field?: string; issue: string }>,
): string | null {
  const detail = details.find((d) => d.field === "retryAt");
  return detail ? detail.issue : null;
}

export function instanceTestRateLimitedSentence(retryAt: string | null, zone: string): string {
  const base =
    "One test every 30 seconds, 10 an hour for this instance and 60 an hour for this tenant.";
  return retryAt ? `${base} Try again at ${clock(retryAt, zone)}.` : base;
}

export function emailTestRateLimitedSentence(retryAt: string | null, zone: string): string {
  const base = "Five test emails were sent in the last hour.";
  return retryAt ? `${base} Try again at ${clock(retryAt, zone)}.` : base;
}
